import * as React from "react";

import {
  CheckIcon,
  PlusIcon,
  SearchIcon,
  PlayIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  PhoneIcon,
  RefreshIcon,
} from "@heroicons/react/solid";

import { IoMap } from "@react-icons/all-files/io5/IoMap";
import { IoOptionsOutline } from "@react-icons/all-files/io5/IoOptionsOutline";
import { FaCircleNotch } from "@react-icons/all-files/fa/FaCircleNotch";

const sizes: any = {
  large: "text-17 py-[10.5px] px-[20px] leading-[133%]",
  medium: "text-15 py-[10px] px-[10px] leading-[133%]",
  small: "text-13 py-[8px] px-[24px] leading-[133%]",
};

const widths: any = {
  full: "w-full",
  auto: "w-fit",
};

const colors: any = {
  blue: "bg-blue",
  blue10: "bg-blue/10",
  green: "bg-green",
  green10: "bg-green/10",
  orange: "bg-orange",
  orange10: "bg-orange/10",
  red: "bg-red",
  red10: "bg-red/10",
  white: "bg-white",
  white90: "bg-white/90",
  white80: "bg-white/80",
  grey: "bg-grey",
  grey2: "bg-grey2",
  grey3: "bg-grey3",
  grey4: "bg-grey4",
  lightgreen: "bg-lightgreen",
  darkblue: "bg-darkblue",
  black: "bg-black",
  black90: "bg-black/90",
  black80: "bg-black/80",
  black70: "bg-black/70",
  black60: "bg-black/60",
  black50: "bg-black/50",
  black40: "bg-black/40",
  black30: "bg-black/30",
  black20: "bg-black/20",
  black10: "bg-black/10",
  black05: "bg-black/05",
  bordergrey1: "bg-bordergrey1",
  bordergrey2: "bg-bordergrey2",
  qualityvgood: "bg-qualityvgood",
  qualitygood: "bg-qualitygood",
  qualityok: "bg-qualityok",
  qualitypoor: "bg-qualitypoor",
  qualityvpoor: "bg-qualityvpoor",
  alert: "bg-alert",
  alertbg: "bg-alertbg",
};

const invertColors: any = {
  blue: "!text-blue border-blue",
  blue10: "!text-blue/10 border-blue/10",
  green: "!text-green border-green",
  green10: "!text-green/10 border-green/10",
  orange: "!text-orange border-orange",
  orange10: "!text-orange/10 border-orange/10",
  red: "!text-red border-red",
  red10: "!text-red/10 border-red/10",
  white: "!text-white border-white",
  white90: "!text-white/90 border-white/90",
  white80: "!text-white/80 border-white/80",
  grey: "!text-grey border-grey",
  grey2: "!text-grey2 border-grey2",
  grey3: "!text-grey3 border-grey3",
  grey4: "!text-grey4 border-grey4",
  grey5: "!text-grey5 border-grey5",
  grey6: "!text-grey6 border-grey6",
  grey7: "!text-grey7 border-grey7",
  lightgreen: "!text-lightgreen  border-lightgreen",
  darkblue: "!text-darkblue border-darkblue",
  black: "!text-black border-black",
  black90: "!text-black/90 border-black/90",
  black80: "!text-black/80 border-black/80",
  black70: "!text-black/70 border-black/70",
  black60: "!text-black/60 border-black/60",
  black50: "!text-black/50 border-black/50",
  black40: "!text-black/40 border-black/40",
  black30: "!text-black/30 border-black/30",
  black20: "!text-black/20 border-black/20",
  black10: "!text-black/10 border-black/10",
  black05: "!text-black/05 border-black/05",
  bordergrey1: "!text-bordergrey1 border-bordergrey1",
  bordergrey2: "!text-bordergrey2 border-bordergrey2",
  qualityvgood: "!text-qualityvgood border-qualityvgood",
  qualitygood: "!text-qualitygood border-qualitygood",
  qualityok: "!text-qualityok border-qualityok",
  qualitypoor: "!text-qualitypoor border-qualitypoor",
  qualityvpoor: "!text-qualityvpoor border-qualityvpoor",
  alert: "!text-alert border-alert",
  alertbg: "!text-alertbg border-alertbg",
};

interface IProps {
  size: string;
  color?: string;
  textColor?: string;
  icon?: string;
  iconSide?: string;
  disabled?: boolean;
  width?: string;
  renderAs?: string;
  invert?: boolean;
  loading?: boolean;
  submit?: boolean;
  text?: string | React.ReactNode;
  className?: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

export default function Button({
  color = "blue",
  textColor,
  invert = false,
  submit = false,
  loading = false,
  icon,
  iconSide = "left",
  width = "auto",
  renderAs = "button",
  size = "large",
  disabled = false,
  text,
  className,
  onClick,
}: IProps) {
  let colorClasses = invert ? invertColors[color] : colors[color];
  let sizeClasses = sizes[size];
  let widthClasses = widths[width];
  let invertStyles = invert ? `border` : `border !border-transparent`;

  const getIcon = () => {
    if (!icon) return null;

    let iconMargin = iconSide === "left" ? "mr-[5px] -ml-0.5" : "-mr-0.5 ml-1";
    let iconClasses = `${iconMargin} h-5 w-5`;
    switch (icon) {
      case "filter":
        return <IoOptionsOutline className={iconClasses} aria-hidden="true" />;
      case "map":
        return <IoMap className={iconClasses} aria-hidden="true" />;
      case "play":
        return <PlayIcon className={iconClasses} aria-hidden="true" />;
      case "search":
        return <SearchIcon className={iconClasses} aria-hidden="true" />;
      case "phone":
        return <PhoneIcon className={iconClasses} aria-hidden="true" />;
      case "check":
        return <CheckIcon className={iconClasses} aria-hidden="true" />;
      case "plus":
        return <PlusIcon className={iconClasses} aria-hidden="true" />;
      case "chevron-right":
        return <ChevronRightIcon className={iconClasses} aria-hidden="true" />;
      case "chevron-left":
        return <ChevronLeftIcon className={iconClasses} aria-hidden="true" />;
    }
  };

  let style = `justify-center font-semibold inline-flex items-center mb-4 ${
    textColor ? textColor : "text-white"
  } rounded-[4px] disabled:opacity-50 ${sizeClasses || ""} ${
    colorClasses || ""
  } ${invertStyles} ${widthClasses || ""} ${className || ""}`;

  if (renderAs) {
    const Element = `${renderAs || "div"}` as keyof JSX.IntrinsicElements;

    return (
      <Element
        disabled={disabled}
        className={style}
        onClick={(e: any) => {
          if (loading) return;
          onClick(e);
        }}
      >
        {iconSide === "left" && getIcon()}
        {text}
        {iconSide === "right" && !loading && getIcon()}
        {loading && <FaCircleNotch className="h-4 w-4 animate-spin ml-1" />}
      </Element>
    );
  }

  return (
    <button
      disabled={disabled}
      type={submit ? "submit" : "button"}
      className={style}
      onClick={(e) => {
        if (loading) return;
        onClick(e);
      }}
    >
      {iconSide === "left" && getIcon()}
      {text}
      {iconSide === "right" && !loading && getIcon()}
      {loading && <RefreshIcon className="h-4 w-4 animate-spin ml-1" />}
    </button>
  );
}
